import { calcFuelInvoiceTotal } from '../services/fleetFuelInvoicesService';

export const FUEL_INVOICE_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

export const FUEL_INVOICE_MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec'
];

export const FUEL_INVOICE_PAYMENT_STATUSES = [
  'Not processed',
  'Pending',
  'Processed',
  'Paid'
];

export const FUEL_INVOICE_PROJECTS = ['HEP IIB (Lot 10)', 'HEP IIB (Lot 8)'];

export const buildFuelInvoiceYearOptions = (extraYears = []) => {
  const current = new Date().getFullYear();
  const years = new Set();
  for (let y = current + 1; y >= current - 8; y -= 1) years.add(String(y));
  (extraYears || []).forEach((y) => {
    const s = String(y || '').trim();
    if (/^\d{4}$/.test(s)) years.add(s);
  });
  return [...years].sort((a, b) => Number(b) - Number(a));
};

export const normalizeFleetProjectFilter = (value) => {
  const s = String(value || '').trim();
  if (!s || s.toLowerCase() === 'all') return 'all';
  return s;
};

/** Monthly totals for the fleet dashboard / fuel report trend chart. */
export const buildFuelInvoiceTrendData = (rows, { projectFilter = 'all', year = 'All' } = {}) => {
  const project = normalizeFleetProjectFilter(projectFilter);
  const filtered =
    project === 'all'
      ? rows
      : rows.filter((r) => String(r.project || '').trim() === project);

  const byMonth = new Map();
  filtered.forEach((row) => {
    const monthName = String(row.month || '').trim();
    const rowYear = String(row.year || '').trim();
    if (!monthName || !rowYear) return;
    if (year !== 'All' && rowYear !== String(year)) return;

    const monthIdx = FUEL_INVOICE_MONTHS.findIndex(
      (m) => m.toLowerCase() === monthName.toLowerCase()
    );
    if (monthIdx < 0) return;

    const key = `${rowYear}-${String(monthIdx + 1).padStart(2, '0')}`;
    const amount =
      Number(row.totalInvoiceAmount) ||
      calcFuelInvoiceTotal(row.fuelAmount, row.managementFee, row.vat);

    const existing = byMonth.get(key) || {
      month: key,
      label: `${FUEL_INVOICE_MONTH_SHORT[monthIdx]} ${rowYear}`,
      cost: 0,
      invoiceCount: 0
    };
    existing.cost += amount;
    existing.invoiceCount += 1;
    byMonth.set(key, existing);
  });

  return [...byMonth.values()].sort((a, b) => a.month.localeCompare(b.month));
};

export const getFleetFuelInvoiceProjectOptions = (rows) => {
  const set = new Set(FUEL_INVOICE_PROJECTS);
  rows.forEach((r) => {
    const p = String(r.project || '').trim();
    if (p) set.add(p);
  });
  return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
};

export const getFleetFuelInvoiceYearOptions = (rows) => {
  const years = new Set();
  rows.forEach((r) => {
    const y = String(r.year || '').trim();
    if (/^\d{4}$/.test(y)) years.add(y);
  });
  return buildFuelInvoiceYearOptions([...years]);
};

export const formatFleetFuelTSh = (n) => {
  if (n === null || n === undefined || n === '' || Number(n) === 0) return '';
  return `TSh ${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

export function fuelProjectBadgeClass(project) {
  const t = String(project || '').trim();
  if (!t) return 'rental-project--empty';
  const key = t.toLowerCase().replace(/[()]/g, ' ').replace(/\s+/g, ' ').trim();
  const known = {
    admin: 'rental-project--admin',
    bts: 'rental-project--bts',
    helios: 'rental-project--helios',
    fttm: 'rental-project--fttm',
    'rea 2c': 'rental-project--rea2c',
    'hep iib lot 8': 'rental-project--hep8',
    'hep iib lot 10': 'rental-project--hep10'
  };
  if (known[key]) return known[key];
  let h = 0;
  for (let i = 0; i < t.length; i += 1) h = (h * 31 + t.charCodeAt(i)) >>> 0;
  return `rental-project--alt${h % 6}`;
}

export function fuelPaymentStatusClass(status) {
  const s = String(status || '').trim().toLowerCase();
  if (!s) return 'fmct-fuel-status--empty';
  if (s === 'not processed') return 'fmct-fuel-status--not-processed';
  if (s === 'pending') return 'fmct-fuel-status--pending';
  if (s === 'processed') return 'fmct-fuel-status--processed';
  if (s === 'paid') return 'fmct-fuel-status--paid';
  return 'fmct-fuel-status--other';
}

export const fuelDatePaidToInputValue = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const m = raw.match(/^(\d{1,2})[-/ ]([A-Za-z]{3})[-/ ](\d{2}|\d{4})$/);
  if (!m) return '';
  const day = String(Number(m[1])).padStart(2, '0');
  const monIdx = FUEL_INVOICE_MONTH_SHORT.findIndex(
    (s) => s.toLowerCase() === m[2].toLowerCase()
  );
  if (monIdx < 0) return '';
  let year = Number(m[3]);
  if (year < 100) year += year >= 70 ? 1900 : 2000;
  return `${year}-${String(monIdx + 1).padStart(2, '0')}-${day}`;
};

export const fuelDateInputToPaidLabel = (iso) => {
  const raw = String(iso || '').trim();
  if (!raw || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  const [y, mo, d] = raw.split('-');
  const mon = FUEL_INVOICE_MONTH_SHORT[Number(mo) - 1];
  if (!mon) return '';
  return `${Number(d)}-${mon}-${String(y).slice(-2)}`;
};

export const emptyFuelInvoiceForm = () => ({
  invoiceNo: '',
  month: '',
  year: '',
  project: '',
  fuelAmount: '',
  managementFee: '',
  vat: '',
  po: '',
  paymentStatus: 'Not processed',
  datePaid: '',
  prNoJazzcom: ''
});

export const moneyFieldValue = (n) => {
  if (n === null || n === undefined || n === '' || Number(n) === 0) return '';
  return Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 });
};

export const cellInputWidthCh = (value, minCh = 6) =>
  Math.max(minCh, String(value || '').length + 1);
